import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const API = 'https://vantage-production-4475.up.railway.app/api';

const TIERS = ['none', 'starter', 'creator', 'pro', 'agency', 'enterprise'];

const TIER_COLORS = {
  none: '#64748b',
  starter: '#00c853',
  creator: '#00bcd4',
  pro: '#7c4dff',
  agency: '#ff6d00',
  enterprise: '#f50057'
};

const TIER_PRICES = {
  none: 'No subscription',
  starter: 'Rs 2,000/month',
  creator: 'Rs 4,500/month',
  pro: 'Rs 8,000/month',
  agency: 'Rs 15,000/month',
  enterprise: 'Rs 25,000/month'
};

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({
    total: 0, active: 0, inactive: 0,
    starter: 0, creator: 0, pro: 0, agency: 0, enterprise: 0
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const getToken = () => localStorage.getItem('vantage_token');

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setUsers(data.data);
        calculateStats(data.data);
      } else {
        toast.error('Failed to load users');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userList) => {
    const s = {
      total: userList.length,
      active: userList.filter(u => u.is_active).length,
      inactive: userList.filter(u => !u.is_active).length,
      starter: userList.filter(u => u.subscription_tier === 'starter').length,
      creator: userList.filter(u => u.subscription_tier === 'creator').length,
      pro: userList.filter(u => u.subscription_tier === 'pro').length,
      agency: userList.filter(u => u.subscription_tier === 'agency').length,
      enterprise: userList.filter(u => u.subscription_tier === 'enterprise').length
    };
    setStats(s);
  };

  const handleUpdateUser = async (userId, isActive, tier) => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/auth/users/${userId}/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ is_active: isActive, tier })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('User updated successfully');
        setShowModal(false);
        fetchUsers();
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setUpdating(false);
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><span className="gradient-text">Admin Panel</span></h1>
        <p>Manage users, subscriptions and platform access</p>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px', marginBottom: '24px'
      }}>
        {[
          { label: 'Total Users', value: stats.total, color: '#00c853' },
          { label: 'Active', value: stats.active, color: '#00bcd4' },
          { label: 'Inactive', value: stats.inactive, color: '#ef4444' },
          { label: 'Starter', value: stats.starter, color: '#00c853' },
          { label: 'Creator', value: stats.creator, color: '#00bcd4' },
          { label: 'Pro', value: stats.pro, color: '#7c4dff' },
          { label: 'Agency', value: stats.agency, color: '#ff6d00' },
          { label: 'Enterprise', value: stats.enterprise, color: '#f50057' }
        ].map((stat, i) => (
          <div key={i} className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{
              fontFamily: 'Syne, sans-serif', fontSize: '28px',
              fontWeight: '800', color: stat.color
            }}>
              {stat.value}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Search and Refresh */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <input
          className="form-control"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <button className="btn btn-secondary" onClick={fetchUsers}>
          🔄 Refresh
        </button>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">All Users ({filteredUsers.length})</span>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            No users found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Name', 'Email', 'Status', 'Tier', 'Joined', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px', textAlign: 'left',
                      fontSize: '12px', fontWeight: '700',
                      color: 'var(--text-secondary)', whiteSpace: 'nowrap'
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} style={{
                    borderBottom: '1px solid var(--border)',
                    transition: 'background 0.2s'
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>
                        {user.name}
                        {user.is_admin && (
                          <span style={{
                            marginLeft: '8px', fontSize: '10px',
                            background: 'rgba(245,0,87,0.2)', color: '#f50057',
                            padding: '2px 6px', borderRadius: '4px', fontWeight: '700'
                          }}>ADMIN</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {user.email}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                        background: user.is_active ? 'rgba(0,200,83,0.1)' : 'rgba(239,68,68,0.1)',
                        color: user.is_active ? '#00c853' : '#ef4444',
                        border: '1px solid ' + (user.is_active ? 'rgba(0,200,83,0.3)' : 'rgba(239,68,68,0.3)')
                      }}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                        background: `${TIER_COLORS[user.subscription_tier || 'none']}20`,
                        color: TIER_COLORS[user.subscription_tier || 'none'],
                        border: `1px solid ${TIER_COLORS[user.subscription_tier || 'none']}40`,
                        textTransform: 'capitalize'
                      }}>
                        {user.subscription_tier || 'none'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => { setSelectedUser(user); setShowModal(true); }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Management Modal */}
      {showModal && selectedUser && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: '20px',
            padding: '32px', maxWidth: '480px', width: '100%',
            border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)' }}>
                Manage User
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)', border: 'none',
                  color: 'var(--text-primary)', width: '32px', height: '32px',
                  borderRadius: '50%', cursor: 'pointer', fontSize: '16px'
                }}>×</button>
            </div>

            <div style={{
              padding: '16px', borderRadius: '12px',
              background: 'var(--bg-dark)', marginBottom: '24px'
            }}>
              <div style={{ fontWeight: '700', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                {selectedUser.name}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                {selectedUser.email}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Joined: {new Date(selectedUser.created_at).toLocaleDateString()}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">Account Status</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { label: '✅ Active', value: true, color: '#00c853' },
                  { label: '❌ Inactive', value: false, color: '#ef4444' }
                ].map(opt => (
                  <div key={opt.label}
                    onClick={() => setSelectedUser({ ...selectedUser, is_active: opt.value })}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', cursor: 'pointer',
                      textAlign: 'center', fontSize: '13px', fontWeight: '700',
                      background: selectedUser.is_active === opt.value
                        ? `${opt.color}20` : 'var(--bg-dark)',
                      border: '1px solid ' + (selectedUser.is_active === opt.value
                        ? opt.color + '60' : 'var(--border)'),
                      color: selectedUser.is_active === opt.value ? opt.color : 'var(--text-secondary)',
                      transition: 'all 0.2s'
                    }}>
                    {opt.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label className="form-label">Subscription Tier</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {TIERS.map(tier => (
                  <div key={tier}
                    onClick={() => setSelectedUser({ ...selectedUser, subscription_tier: tier })}
                    style={{
                      padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: selectedUser.subscription_tier === tier
                        ? `${TIER_COLORS[tier]}15` : 'var(--bg-dark)',
                      border: '1px solid ' + (selectedUser.subscription_tier === tier
                        ? TIER_COLORS[tier] + '50' : 'var(--border)'),
                      transition: 'all 0.2s'
                    }}>
                    <span style={{
                      fontSize: '14px', fontWeight: '700', textTransform: 'capitalize',
                      color: selectedUser.subscription_tier === tier
                        ? TIER_COLORS[tier] : 'var(--text-secondary)'
                    }}>
                      {tier}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {TIER_PRICES[tier]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>
                Cancel
              </button>
              <button className="btn btn-primary"
                onClick={() => handleUpdateUser(
                  selectedUser.id,
                  selectedUser.is_active,
                  selectedUser.subscription_tier
                )}
                disabled={updating}
                style={{ flex: 2 }}>
                {updating ? 'Updating...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}